import { motion, useAnimation } from "framer-motion";
import { useEffect, useRef, useCallback } from "react";

interface CurveProps {
  onProgress?: (progress: number) => void;
  disableAnimation?: boolean;
  pdfMode?: boolean;
}

export default function Curve({
  onProgress,
  disableAnimation = false,
  pdfMode = false,
}: CurveProps) {
  const controls = useAnimation();
  const animationFrameRef = useRef<number | undefined>(undefined);
  const lastProgressRef = useRef<number>(-1);

  const updateProgress = useCallback(
    (progress: number) => {
      if (Math.abs(progress - lastProgressRef.current) > 0.01) {
        lastProgressRef.current = progress;
        onProgress?.(progress);
      }
    },
    [onProgress]
  );

  useEffect(() => {
    if (disableAnimation) {
      controls.set({ pathLength: 1, opacity: 1 });
      updateProgress(1);
      return () => {
        if (animationFrameRef.current !== undefined) {
          cancelAnimationFrame(animationFrameRef.current);
        }
      };
    }

    const animatePath = async () => {
      const startTime = Date.now();
      const duration = 1000;

      const tick = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        updateProgress(progress);
        if (progress < 1) {
          animationFrameRef.current = requestAnimationFrame(tick);
        } else {
          updateProgress(1);
        }
      };

      await controls.start({
        pathLength: 1,
        transition: { duration: duration / 1000, ease: "easeInOut" },
      });
      tick();
    };

    animatePath();

    return () => {
      if (animationFrameRef.current !== undefined) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [controls, updateProgress, disableAnimation]);

  const svgClass = "absolute inset-0 h-full w-full";

  return (
    <div className="pointer-events-none absolute inset-0">
      <svg
        className={svgClass}
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
      >
        <defs>
          <linearGradient id="lifeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#1E88E5" />
            <stop offset="18%" stopColor="#00BCD4" />
            <stop offset="38%" stopColor="#34C759" />
            <stop offset="58%" stopColor="#B4D62C" />
            <stop offset="74%" stopColor="#FFC107" />
            <stop offset="88%" stopColor="#FF7043" />
            <stop offset="100%" stopColor="#8E24AA" />
          </linearGradient>

          <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="1.5" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <motion.path
          initial={
            disableAnimation
              ? { pathLength: 1, opacity: 0.12 }
              : { pathLength: 0, opacity: 0 }
          }
          animate={
            disableAnimation ? { pathLength: 1, opacity: 0.12 } : controls
          }
          d="
          M5 83
          C12 74 18 66 24 57
          S36 40 46 22
          S60 12 72 28
          S86 55 96 84
          "
          fill="none"
          stroke="url(#lifeGradient)"
          strokeWidth="1.5"
          opacity="0.12"
          filter="url(#glow)"
        />

        <motion.path
          initial={disableAnimation ? { pathLength: 1 } : { pathLength: 0 }}
          animate={disableAnimation ? { pathLength: 1 } : controls}
          d="
          M5 83
          C12 74 18 66 24 57
          S36 40 46 22
          S60 12 72 28
          S86 55 96 84
          "
          fill="none"
          stroke="url(#lifeGradient)"
          strokeWidth={pdfMode ? "0.8" : "0.5"}
          strokeLinecap="round"
        />
      </svg>

      <div className="bg-yellow-300/12 absolute left-1/2 top-[18%] h-24 w-24 -translate-x-1/2 rounded-full blur-[50px] md:h-32 md:w-32" />
      <div className="bg-cyan-300/12 absolute left-[25%] top-[40%] h-24 w-24 rounded-full blur-[50px] md:h-32 md:w-32" />
      <div className="bg-orange-300/12 absolute right-[15%] top-[35%] h-28 w-28 rounded-full blur-[50px] md:h-40 md:w-40" />
    </div>
  );
}
